import React, { useRef, useState } from 'react';
import './ContactFormComponent.css';
import ReCAPTCHA from 'react-google-recaptcha';
import { Notification } from './NotificationLoginComponent';
import { SendContactEmail } from '../Backend/ApiCalls';

const initialFormState = {
    name: '',
    lastName: '',
    email: '',
    phone: '',
    reason: '',
    otherReason: '',
    comment: '',
};

const buildSubject = ({ reason, otherReason }) => {
    if (reason === 'Otros...') {
        return `Contacto web - ${otherReason.trim() || 'Otros'}`;
    }

    return `Contacto web - ${reason || 'Sin motivo'}`;
};

const buildMessage = ({ lastName, phone, reason, otherReason, comment }) => {
    const resolvedReason = reason === 'Otros...' ? otherReason.trim() || 'Otros' : reason;

    return [
        `Apellidos: ${lastName.trim() || 'No indicado'}`,
        `Teléfono: ${phone.trim() || 'No indicado'}`,
        `Motivo: ${resolvedReason || 'No indicado'}`,
        '',
        'Comentario:',
        comment.trim(),
    ].join('\n');
};

export const ContactFormComponent = ({ handleNotification }) => {
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
    const recaptchaRef = useRef(null);

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [notificationMessage, setNotificationMessage] = useState('');
    const [showNotification, setShowNotification] = useState(false);
    const [isErrorNoti, setIsErrorNoti] = useState(false);
    const [captchaToken, setCaptchaToken] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.match(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/)) {
            newErrors.name = 'El nombre no puede contener números ni caracteres especiales.';
        }

        if (!formData.lastName.match(/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/)) {
            newErrors.lastName = 'Los apellidos no pueden contener números ni caracteres especiales.';
        }

        if (!formData.email.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
            newErrors.email = 'El correo electrónico no es válido.';
        }

        if (formData.phone && !formData.phone.match(/^\d{9}$/)) {
            newErrors.phone = 'El número de teléfono debe contener 9 dígitos.';
        }

        if (!formData.reason.trim()) {
            newErrors.reason = 'Selecciona un motivo.';
        }

        if (formData.reason === 'Otros...' && formData.otherReason.trim() === '') {
            newErrors.otherReason = 'Por favor, especifica el motivo.';
        }

        if (formData.comment.trim() === '') {
            newErrors.comment = 'El comentario no puede estar vacío.';
        }

        if (recaptchaSiteKey && !captchaToken) {
            newErrors.captcha = 'Verifica el CAPTCHA.';
        }

        return newErrors;
    };

    const pushNotification = (message, type) => {
        setNotificationMessage(message);
        setIsErrorNoti(type === 'error');
        setShowNotification(true);
        handleNotification?.(message, type);

        window.setTimeout(() => {
            setShowNotification(false);
        }, 5000);
    };

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token || '');
        setErrors((prev) => {
            const { captcha, ...rest } = prev;
            return rest;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                subject: buildSubject(formData),
                message: buildMessage(formData),
                captchaToken,
            };

            const { response, data } = await SendContactEmail(payload);

            if (!response.ok) {
                throw new Error(data?.Error || 'No se pudo enviar el mensaje.');
            }

            setFormData(initialFormState);
            setCaptchaToken('');
            setErrors({});
            recaptchaRef.current?.reset();

            pushNotification('¡Mensaje enviado correctamente! Te responderemos pronto.', 'success');
        } catch (error) {
            console.error(error);
            pushNotification(error.message || 'Error al enviar el mensaje. Inténtalo de nuevo.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='recipient'>
            {showNotification && (
                <Notification
                    message={notificationMessage}
                    type={isErrorNoti ? 'error' : 'success'}
                />
            )}

            <h1 className='titulo-page'>Formulario de Contacto</h1>

            <div className="contact-form-container">
                <form onSubmit={handleSubmit} className='contact-form'>
                    <div className="contact-form-columns">
                        <div className="contact-column">
                            <div className='contact-form-group'>
                                <label>Nombre:</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="contact-input"
                                    required
                                />
                                {errors.name && <span className="error-message">{errors.name}</span>}
                            </div>

                            <div className='contact-form-group'>
                                <label>Apellidos:</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    className="contact-input"
                                    required
                                />
                                {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                            </div>

                            <div className='contact-form-group'>
                                <label>Correo:</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="contact-input"
                                    required
                                />
                                {errors.email && <span className="error-message">{errors.email}</span>}
                            </div>
                        </div>

                        <div className="contact-column">
                            <div className='contact-form-group'>
                                <label>Teléfono:</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="contact-input"
                                />
                                {errors.phone && <span className="error-message">{errors.phone}</span>}
                            </div>

                            <div className='contact-form-group'>
                                <label>Motivo:</label>
                                <select
                                    name="reason"
                                    value={formData.reason}
                                    onChange={handleChange}
                                    className="contact-select"
                                    required
                                >
                                    <option value="">Selecciona un motivo</option>
                                    <option value="Sobre la falla">Sobre la falla</option>
                                    <option value="Incidencia">Incidencia</option>
                                    <option value="Queja">Queja</option>
                                    <option value="Sugerencia">Sugerencia</option>
                                    <option value="Bug">Bug</option>
                                    <option value="Otros...">Otros...</option>
                                </select>
                                {errors.reason && <span className="error-message">{errors.reason}</span>}
                            </div>

                            {formData.reason === 'Otros...' && (
                                <div className='contact-form-group'>
                                    <label>Otro:</label>
                                    <input
                                        type="text"
                                        name="otherReason"
                                        value={formData.otherReason}
                                        onChange={handleChange}
                                        className="contact-input"
                                    />
                                    {errors.otherReason && <span className="error-message">{errors.otherReason}</span>}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='contact-form-group contact-form-comentario'>
                        <label>Comentario:</label>
                        <textarea
                            name="comment"
                            value={formData.comment}
                            onChange={handleChange}
                            className="contact-textarea"
                            required
                        />
                        {errors.comment && <span className="error-message">{errors.comment}</span>}
                    </div>

                    {recaptchaSiteKey ? (
                        <>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={recaptchaSiteKey}
                                onChange={handleCaptchaChange}
                            />
                            {errors.captcha && <span className="error-message">{errors.captcha}</span>}
                        </>
                    ) : null}

                    <button type="submit" className='contact-button' disabled={isSubmitting}>
                        {isSubmitting ? 'Enviando...' : 'Enviar'}
                    </button>
                </form>
            </div>
        </div>
    );
};
